import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Hourglass, Circle } from 'lucide-react';
import { api } from '@/lib/api';
import type { Paginated, MyWorkItem } from '@/lib/types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EstadoSelector } from '@/components/domain/EstadoSelector';
import { dueStatus, fmtShortDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ItemDetailDialog } from '@/components/domain/ItemDetailDialog';

interface Filters {
  search: string;
  range: 'overdue' | 'today' | 'week' | 'month' | 'all';
  estado?: 'sin_iniciar' | 'en_proceso' | 'completado' | 'detenido' | undefined;
  fase?: string | undefined;
  page: number;
  perPage: number;
}

const DEFAULT_FILTERS: Filters = {
  search: '',
  range: 'all',
  estado: undefined,
  fase: undefined,
  page: 1,
  perPage: 10,
};

export function MyWorkPage() {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['my-work', filters],
    queryFn: () =>
      api<Paginated<MyWorkItem>>('/my-work', {
        params: {
          page: filters.page,
          perPage: filters.perPage,
          search: filters.search || undefined,
          range: filters.range,
          estado: filters.estado,
          fase: filters.fase,
        },
      }),
  });

  // Fases disponibles para el filtro (solo las que tienen subtareas activas)
  const fasesQ = useQuery({
    queryKey: ['my-work-fases'],
    queryFn: () => api<{ fase: string; count: number }[]>('/my-work/fases'),
    staleTime: 60_000,
  });

  // KPIs (queries paralelos para contadores específicos)
  const kpis = useKpis();

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6 max-w-7xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold">Mi trabajo</h1>
        <p className="text-sm text-ink-400 mt-1">
          Todas las subtareas que tienes asignadas, en un solo lugar
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard
          icon={AlertTriangle}
          label="Vencidas o vencen hoy"
          value={kpis.urgent}
          variant="danger"
        />
        <KpiCard icon={Circle} label="Pendientes" value={kpis.pendientes} />
        <KpiCard icon={Hourglass} label="Total asignadas" value={kpis.total} variant="muted" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Buscar subtarea o booking…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
          className="w-72"
        />
        <Select
          value={filters.range}
          onValueChange={(v) => setFilters({ ...filters, range: v as any, page: 1 })}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las fechas</SelectItem>
            <SelectItem value="overdue">Vencidas</SelectItem>
            <SelectItem value="today">Hoy</SelectItem>
            <SelectItem value="week">Próximos 7 días</SelectItem>
            <SelectItem value="month">Próximos 30 días</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.estado === 'completado' ? 'completado' : '__pending__'}
          onValueChange={(v) =>
            setFilters({
              ...filters,
              estado: v === 'completado' ? 'completado' : undefined,
              page: 1,
            })
          }
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__pending__">Pendientes</SelectItem>
            <SelectItem value="completado">Completadas</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.fase ?? '__any__'}
          onValueChange={(v) =>
            setFilters({ ...filters, fase: v === '__any__' ? undefined : v, page: 1 })
          }
        >
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Fase" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Todas las fases</SelectItem>
            {(fasesQ.data ?? []).map((f) => (
              <SelectItem key={f.fase} value={f.fase}>
                {f.fase} ({f.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(filters.search ||
          filters.range !== 'all' ||
          filters.estado !== undefined ||
          filters.fase !== undefined) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilters(DEFAULT_FILTERS)}
            title="Limpiar todos los filtros"
          >
            Limpiar
          </Button>
        )}
      </div>

      {/* Tabla */}
      <div className="rounded-lg border border-ink-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Subtarea</th>
              <th className="text-left px-3 py-2 font-medium w-44">Booking</th>
              <th className="text-left px-3 py-2 font-medium w-36">Fase</th>
              <th className="text-left px-3 py-2 font-medium w-32">Estado</th>
              <th className="text-left px-3 py-2 font-medium w-28">Vence</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-ink-400">
                  Cargando…
                </td>
              </tr>
            )}
            {!isLoading && data?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-16 text-center">
                  <MyWorkEmptyState
                    hasFilters={
                      !!filters.search ||
                      filters.range !== 'all' ||
                      filters.estado !== undefined ||
                      filters.fase !== undefined
                    }
                    onClearFilters={() => setFilters(DEFAULT_FILTERS)}
                    kpiTotals={kpis.pendientes}
                  />
                </td>
              </tr>
            )}
            {data?.items.map((it) => {
              const due = dueStatus(it.fechaLimite);
              const urgent = due === 'vencida' || due === 'hoy';
              return (
                <tr
                  key={it.id}
                  className={cn(
                    'border-t border-ink-100 hover:bg-surface-2 cursor-pointer',
                    urgent && 'bg-brand-50/40 hover:bg-brand-50',
                  )}
                  onClick={() => setSelectedItem(it.id)}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-baseline gap-2">
                      <span className="tabular text-xs text-ink-400 shrink-0">
                        {it.hierarchyId}
                      </span>
                      <span className="line-clamp-1">{it.nombre}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="font-medium tabular">{it.booking.bookingIdExterno}</div>
                    <div className="text-ink-400 line-clamp-1">{it.booking.clienteNombre}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {it.fase ? (
                      <span
                        className="inline-flex items-center rounded bg-ink-100 px-1.5 py-0.5 text-ink-700"
                        title={it.fase}
                      >
                        {it.fase}
                      </span>
                    ) : (
                      <span className="text-ink-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {/* Mi trabajo solo lista items HOJA del agente actual, así que siempre canEdit=true */}
                    <EstadoSelector
                      itemId={it.id}
                      estado={it.estado}
                      isLeaf={true}
                      canEdit={true}
                    />
                  </td>
                  <td
                    className={cn(
                      'px-3 py-2 tabular text-xs',
                      due === 'vencida' && 'text-brand-700 font-semibold',
                      due === 'hoy' && 'text-brand-500 font-semibold',
                    )}
                  >
                    {due === 'vencida' && '⚠ Vencida · '}
                    {due === 'hoy' && 'Hoy · '}
                    {fmtShortDate(it.fechaLimite)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {data && data.pagination.total > 0 && (
        <Pagination
          page={data.pagination.page}
          perPage={data.pagination.perPage}
          total={data.pagination.total}
          totalPages={data.pagination.totalPages}
          onPageChange={(p) => setFilters({ ...filters, page: p })}
          onPerPageChange={(pp) => setFilters({ ...filters, perPage: pp, page: 1 })}
        />
      )}

      {selectedItem != null && (
        <ItemDetailDialog
          itemId={selectedItem}
          open={selectedItem != null}
          onOpenChange={(o) => !o && setSelectedItem(null)}
        />
      )}
    </div>
  );
}

function useKpis() {
  // Queries paralelos para los contadores principales del header
  const pendientes = useQuery({
    queryKey: ['my-work-count', 'pendientes'],
    // Sin filtro estado → /my-work devuelve no-completadas por default
    queryFn: () => api<Paginated<MyWorkItem>>('/my-work', { params: { perPage: 1 } }),
  });
  const completadas = useQuery({
    queryKey: ['my-work-count', 'completado'],
    queryFn: () =>
      api<Paginated<MyWorkItem>>('/my-work', { params: { estado: 'completado', perPage: 1 } }),
  });
  const urgent = useQuery({
    queryKey: ['my-work-count', 'urgent'],
    queryFn: () =>
      api<Paginated<MyWorkItem>>('/my-work', { params: { range: 'overdue', perPage: 1 } }),
  });

  return {
    pendientes: pendientes.data?.pagination.total ?? 0,
    completadas: completadas.data?.pagination.total ?? 0,
    total: (pendientes.data?.pagination.total ?? 0) + (completadas.data?.pagination.total ?? 0),
    urgent: urgent.data?.pagination.total ?? 0,
  };
}

function MyWorkEmptyState({
  hasFilters,
  onClearFilters,
  kpiTotals,
}: {
  hasFilters: boolean;
  onClearFilters: () => void;
  kpiTotals: number;
}) {
  // 3 escenarios:
  //   1. No tienes filtros y kpiTotals=0 → agente nuevo / sin trabajo
  //   2. Tienes filtros activos → "no hay con estos filtros"
  //   3. Filtros default + completaste todo → "¡buen trabajo!"
  if (hasFilters) {
    return (
      <div className="flex flex-col items-center gap-2 text-ink-400">
        <p>No hay subtareas con estos filtros.</p>
        <button
          onClick={onClearFilters}
          className="text-xs text-brand-500 hover:underline mt-1"
        >
          Limpiar filtros
        </button>
      </div>
    );
  }
  if (kpiTotals === 0) {
    return (
      <div className="flex flex-col items-center gap-3 text-ink-400 max-w-md mx-auto">
        <div className="text-5xl mb-2">📋</div>
        <h3 className="text-base font-semibold text-ink-900">Sin tareas asignadas todavía</h3>
        <p className="text-sm">
          Cuando Cargowise registre un booking a tu nombre, aparecerá aquí
          con sus subtareas listas para palomear.
        </p>
        <p className="text-xs mt-2">
          Mientras tanto, puedes echar un ojo a{' '}
          <a href="/board" className="text-brand-500 hover:underline">tu board</a>{' '}
          para ver bookings en curso.
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-2 text-emerald-700">
      <div className="text-4xl mb-2">✓</div>
      <h3 className="text-base font-semibold">¡Sin pendientes!</h3>
      <p className="text-sm text-ink-400">
        Todas tus subtareas están completadas. Cambia el filtro para ver el histórico.
      </p>
    </div>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  variant = 'default',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  variant?: 'default' | 'danger' | 'muted';
}) {
  return (
    <div className="rounded-lg border border-ink-200 bg-surface-1 p-4">
      <div
        className={cn(
          'text-2xl font-semibold tabular',
          variant === 'danger' && value > 0 && 'text-brand-500',
          variant === 'muted' && 'text-ink-400',
        )}
      >
        {value}
      </div>
      <div className="text-xs text-ink-400 mt-1 flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {label}
      </div>
    </div>
  );
}

function Pagination({
  page,
  perPage,
  total,
  totalPages,
  onPageChange,
  onPerPageChange,
}: {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  onPerPageChange: (pp: number) => void;
}) {
  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="text-sm text-ink-400 tabular">
        Mostrando <span className="text-ink-900 font-medium">{start}-{end}</span> de{' '}
        <span className="text-ink-900 font-medium">{total}</span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          ◀
        </Button>
        <span className="text-sm tabular px-2">
          {page} / {totalPages}
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          ▶
        </Button>
        <Select
          value={String(perPage)}
          onValueChange={(v) => onPerPageChange(Number(v))}
        >
          <SelectTrigger className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="20">20</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
